import React from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { useSEO } from "../lib/useSEO";
import { SERVICED_STATES } from "../data/content";
import { ArrowRight, MapPin } from "lucide-react";

const stateChip = "bg-white border border-[#E5E7EB] rounded-lg px-4 py-3 text-sm text-[#374151] hover:border-[#0066FF] transition-colors flex items-center gap-2";

export default function States() {
  useSEO(
    "Pre-Settlement Funding by State: Where We Serve",
    "Instabridge currently offers non-recourse pre-settlement funding in AZ, CA, FL, MA, MI, NJ, OH, PA and TX. See our service areas and state requirements.",
    { canonical: "https://instabridgefunding.com/states" }
  );

  return (
    <div className="min-h-screen bg-white" data-testid="states-page">
      <Navbar />

      <section className="bg-[#F9FAFB] border-b border-[#E5E7EB] py-16 lg:py-20">
        <div className="mx-auto max-w-5xl px-6 lg:px-10">
          <div className="uppercase-label mb-5">Service areas</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#0A2540] font-light leading-[1.05]" data-testid="states-h1">
            Pre-Settlement Funding: <span className="font-semibold">States We Serve</span>
          </h1>
          <p className="mt-6 text-lg text-[#6B7280] max-w-3xl leading-relaxed" data-testid="states-intro">
            Instabridge currently offers pre-settlement funding in select states across the country. Explore our current service areas below. Funding availability and eligibility are subject to applicable state requirements.
          </p>
        </div>
      </section>

      {/* Featured: Ohio */}
      <section className="py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="uppercase-label mb-5">Featured</div>
          <Link to="/states/ohio" className="block bg-[#0A2540] text-white rounded-2xl p-8 lg:p-12 hover:-translate-y-0.5 transition-transform relative overflow-hidden">
            <div className="absolute -right-20 -bottom-20 w-72 h-72 rounded-full bg-[#0066FF]/30 blur-3xl" />
            <div className="relative grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <div className="uppercase-label !text-white/60 mb-3">Featured state</div>
                <h2 className="font-display text-4xl lg:text-5xl font-light tracking-tight">Ohio pre-settlement funding</h2>
                <p className="mt-4 text-white/80 leading-relaxed">Cleveland, Columbus, Cincinnati, Toledo, Dayton — we fund Ohio plaintiffs across all major metros. Same-day approval common.</p>
                <div className="mt-6 inline-flex items-center gap-2 text-[#0066FF] font-semibold bg-white px-5 h-10 rounded-full">
                  Explore Ohio funding <ArrowRight className="w-4 h-4" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {["Cleveland", "Columbus", "Cincinnati", "Toledo", "Dayton"].map((c) => (
                  <div key={c} className="bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-sm flex items-center gap-2">
                    <MapPin className="w-3.5 h-3.5 text-[#0066FF]" /> {c}
                  </div>
                ))}
              </div>
            </div>
          </Link>
        </div>
      </section>

      {/* All states list */}
      <section className="py-16 bg-[#F9FAFB]">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <h2 className="font-display text-3xl text-[#0A2540] font-light tracking-tight">States We Currently Serve</h2>
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5" data-testid="serviced-states-list">
            {SERVICED_STATES.map((s) => {
              const id = `serviced-state-${s.toLowerCase().replace(/\s+/g, "-")}`;
              const inner = <><MapPin className="w-3.5 h-3.5 text-[#0066FF]" /> {s}</>;
              return s === "Ohio" ? (
                <Link key={s} to="/states/ohio" className={stateChip} data-testid={id}>{inner}</Link>
              ) : (
                <div key={s} className={stateChip} data-testid={id}>{inner}</div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#0A2540] text-white" data-testid="states-expansion-section">
        <div className="mx-auto max-w-3xl px-6 lg:px-10 text-center">
          <h2 className="font-display text-3xl lg:text-4xl font-semibold">Don't see your state?</h2>
          <p className="mt-3 text-white/70">We're continually working to expand our service areas. If your state isn't listed, contact us to learn about future availability.</p>
          <Link to="/contact" className="inline-block mt-8">
            <Button className="bg-white text-[#0A2540] hover:bg-[#0066FF] hover:text-white rounded-full h-12 px-7" data-testid="states-contact-btn">Contact us</Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
